import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, User, Check } from "lucide-react";
import Countdown from "./Countdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RsvpForm() {
  const [nome, setNome] = useState("");
  const [instagram, setInstagram] = useState("");
  const [presenca, setPresenca] = useState("sim");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(0);

  const loadCount = () =>
    axios
      .get(`${API}/rsvp/count`)
      .then((r) => setCount(r.data.confirmados))
      .catch(() => {});

  useEffect(() => {
    loadCount();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (nome.trim().length < 2) {
      setStatus("error");
      setMessage("Por favor, informe seu nome.");
      return;
    }
    setStatus("sending");
    try {
      const { data } = await axios.post(`${API}/rsvp`, {
        nome: nome.trim(),
        presenca,
        instagram: instagram.trim(),
      });
      setMessage(data.message);
      setStatus("done");
      loadCount();
    } catch {
      setStatus("error");
      setMessage("Não foi possível enviar sua resposta. Tente novamente.");
    }
  };

  const reset = () => {
    setNome("");
    setInstagram("");
    setPresenca("sim");
    setStatus("idle");
    setMessage("");
  };

  return (
    <section id="confirmar" data-testid="rsvp-section" className="px-6 py-24 md:py-32">
      <motion.div
        className="max-w-xl mx-auto text-center"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
      >
        <p className="text-xs tracking-[0.45em] uppercase" style={{ color: "var(--muted)" }}>
          Contagem regressiva
        </p>
        <div className="mt-8">
          <Countdown />
        </div>

        <h2 className="font-display gold-text text-3xl sm:text-4xl lg:text-5xl mt-16">Confirme sua presença</h2>
        {count > 0 && (
          <p data-testid="rsvp-count" className="mt-4 text-sm tracking-[0.15em]" style={{ color: "var(--muted)" }}>
            {count} {count === 1 ? "pessoa já confirmou" : "pessoas já confirmaram"} presença
          </p>
        )}

        <AnimatePresence mode="wait">
          {status === "done" ? (
            <motion.div
              key="done"
              data-testid="rsvp-success-message"
              className="lux-card mt-10 px-8 py-12 flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span
                className="inline-flex items-center justify-center w-14 h-14 rounded-full"
                style={{ border: "1px solid var(--gold)" }}
              >
                <Check className="w-6 h-6" style={{ color: "var(--gold)" }} />
              </span>
              <p className="font-display gold-text text-2xl mt-6">{message}</p>
              <button data-testid="rsvp-again-button" onClick={reset} className="lux-btn mt-8">
                Enviar outra resposta
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              data-testid="rsvp-form"
              onSubmit={submit}
              className="mt-10 text-left space-y-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.5 }}
            >
              <label className="block">
                <span
                  className="text-xs tracking-[0.3em] uppercase flex items-center gap-2"
                  style={{ color: "var(--muted)" }}
                >
                  <User className="w-3.5 h-3.5" /> Seu nome
                </span>
                <input
                  data-testid="rsvp-name-input"
                  className="lux-input mt-2"
                  placeholder="Como você se chama?"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  maxLength={120}
                />
              </label>

              <label className="block">
                <span
                  className="text-xs tracking-[0.3em] uppercase flex items-center gap-2"
                  style={{ color: "var(--muted)" }}
                >
                  <Instagram className="w-3.5 h-3.5" /> Seu Instagram (opcional)
                </span>
                <input
                  data-testid="rsvp-instagram-input"
                  className="lux-input mt-2"
                  placeholder="@seuperfil"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  maxLength={60}
                />
              </label>

              <div>
                <span className="text-xs tracking-[0.3em] uppercase" style={{ color: "var(--muted)" }}>
                  Você vai ao evento?
                </span>
                <div className="flex flex-col sm:flex-row gap-4 mt-3">
                  <button
                    type="button"
                    data-testid="rsvp-presence-yes"
                    onClick={() => setPresenca("sim")}
                    className={`lux-pill ${presenca === "sim" ? "lux-pill-active" : ""}`}
                  >
                    Sim, estarei lá
                  </button>
                  <button
                    type="button"
                    data-testid="rsvp-presence-no"
                    onClick={() => setPresenca("nao")}
                    className={`lux-pill ${presenca === "nao" ? "lux-pill-active" : ""}`}
                  >
                    Não poderei ir
                  </button>
                </div>
              </div>

              {status === "error" && (
                <p data-testid="rsvp-error-message" className="text-sm text-red-400 text-center">
                  {message}
                </p>
              )}

              <button data-testid="rsvp-submit-button" type="submit" disabled={status === "sending"} className="lux-btn w-full">
                {status === "sending" ? "Enviando..." : "Enviar confirmação"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
